<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import DenominationCountModal from "./DenominationCountModal.vue";
import { PaymentType, CashierDuty, PosStation } from "../../../../../types/models";
import IconComponent from "../../../../../components/ui/common/IconComponent.vue";
import { api } from "../../../../../api/Api";
import Toast from "../../../../../services/Toast";

const props = defineProps<{
  payment_types: PaymentType[];
  cashierDuty: CashierDuty | null
  posStation: PosStation | undefined;
}>();

const emit = defineEmits<{
  (e: "started", floats: CashierDuty): void;
  (e: "close"): void;
}>();

const showDenominationCount = ref(false);
const floatAmount = computed(() => {
  return cashierFloats.value.reduce((sum, float) => {
    return sum + (Number(float.amount) ?? 0)
  }, 0)
})
const handleDenominationUpdate = (total: number, float: PaymentType) => {
  selectedType.value = float;
  selectedType.value.amount = selectedType.value?.denominations?.reduce((sum, denom) => {
    return sum + denom.multiply * denom.count;
  }, 0);
  selectedType.value.actual_count = total;
};


const endPoint = "revenue/sales/pos/";
const handleSubmit = async () => {
  if (floatAmount.value > 0) {
    const response = await api.post(endPoint + "start-shift", {
    outlets_id: props.posStation?.outlets_id,
    warehouses_id: props.posStation?.outlet.warehouses_id,
    cashier_dutys_id: props.cashierDuty?.id,
    pos_stations_id: props.posStation?.id,
    price_lists_id: props.posStation?.outlet.price_lists_id,
    floats: cashierFloats.value
  });
  if (response.success == true) {
    emit("started", response.data);
  } else {
    Toast.show("Could not retrieve the Products", "warning");
  }
  }
};

const cashierFloats = ref<PaymentType[]>([]);
onMounted(() => {
  props.payment_types.forEach((paymentType: PaymentType) => {
    if (paymentType.allow_over_tender) {
      if (props.cashierDuty) {
        cashierFloats.value.push({
          id: '',
          cashier_dutys_id: props.cashierDuty.id,
          payment_types_id: paymentType.id,
          payment_type: paymentType.name,
          has_denomination: paymentType.has_denomination ?? false,
          amount: 0,
          denominations: paymentType.denominations,
          code: paymentType.code,
          name: paymentType.name,
          actual_count: 0,
          difference: 0,
          has_denominations: paymentType.has_denominations,
          cancelled: [],
          transactions: [],
          unverified: false
        });
      }
    }
  });
});

const selectedType = ref<PaymentType>();
const getDenominationCount = (float: PaymentType) => {
  selectedType.value = float;
  showDenominationCount.value = true;
};

</script>

<template>
  <div
    class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
  >
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
      <!-- Header -->
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
          Enter Cash Float Amount
        </h3>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Please count and enter the starting cash amount for this shift.
        </p>
      </div>

      <!-- Content -->
      <div class="p-6">
        <div class="space-y-4">
          <div>
            <div
              v-for="float in cashierFloats"
              :key="float.id"
              class="grid grid-cols-2 gap-4 mt-1"
            >
              <div>{{ float.payment_type }}</div>
                <div class="flex items-center gap-0 relative">
                  <input
                  type="number"
                  :readonly="float.has_denomination"
                  v-model="float.amount"
                  class="text-right max-w-[120px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:focus:border-blue-500"
                  />
                  <button
                  v-if="float.has_denomination"
                  class="px-2 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center"
                  @click="getDenominationCount(float)"
                  >
                  <IconComponent name="Coins" class="mr-1" />
                  <span>Count</span>
                  </button>
                </div>
            </div>
            <div
              class="grid grid-cols-2 gap-4 mt-3"
            >
              <div>Total</div>
              <div class="flex items-center gap-0 relative">
                <input
                  type="number"
                  :readonly="true"
                  v-model="floatAmount"
                  class="text-right max-w-[120px] w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 dark:focus:border-blue-500"
                />
                <div></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg flex justify-end gap-3"
      >
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          @click="handleSubmit"
          :disabled="floatAmount <= 0"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Start Shift
        </button>
      </div>
    </div>

    <DenominationCountModal
      :payment-type="selectedType"
      v-if="showDenominationCount"
      @update="handleDenominationUpdate"
      @close="showDenominationCount = false"
    />

  </div>
</template>
<style scoped>
.input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.input-container input {
  padding-right: 50px; /* Space for the button */
  box-sizing: border-box;
}

.input-container button {
  position: absolute;
  right: 0;
  height: 100%;
}

.input-container .button-hidden {
  padding-right: 10px; /* Adjust if necessary */
}
</style>
